import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const AUTH_HINT_KEY = 'lgra.auth.hint';
export const SESSION_EXPIRED_EVENT = 'lgra:session-expired';

export const readAuthHint = () => {
  try { return window.localStorage.getItem(AUTH_HINT_KEY) === '1'; } catch { return false; }
};
export const writeAuthHint = (on) => {
  try { on ? window.localStorage.setItem(AUTH_HINT_KEY, '1') : window.localStorage.removeItem(AUTH_HINT_KEY); } catch { /* silent */ }
};

// Single-flight refresh shared by every axios instance in the app.
let refreshing = null;
let refreshedAt = 0;
const attached = new WeakSet();

const isAuthCall = (url) => /\/api\/auth\/(login|refresh|logout)\b/.test(url) || /\/admin\/login\b/.test(url);

const refreshOnce = () => {
  if (!refreshing && Date.now() - refreshedAt > 5000) {
    refreshing = axios
      .post(`${API}/auth/refresh`, {}, { withCredentials: true, timeout: 15000, __retried: true })
      .then((r) => { refreshedAt = Date.now(); return r; })
      .finally(() => { refreshing = null; });
  }
  return refreshing || Promise.resolve();
};

/** Install the 401 → /auth/refresh → replay interceptor on an axios instance (idempotent). */
export const attachAuthRefresh = (instance) => {
  if (attached.has(instance)) return instance;
  attached.add(instance);
  instance.interceptors.response.use(
    (r) => r,
    async (error) => {
      const status = error?.response?.status;
      const cfg = error?.config;
      const url = String(cfg?.url || '');
      if (status !== 401 || !cfg || cfg.__retried || isAuthCall(url) || !readAuthHint()) {
        return Promise.reject(error);
      }
      try {
        await refreshOnce();
      } catch {
        writeAuthHint(false);
        try { window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT)); } catch { /* noop */ }
        return Promise.reject(error);
      }
      return instance({ ...cfg, __retried: true, withCredentials: true });
    },
  );
  return instance;
};

/** Axios instance with cookies + silent session renewal already wired. */
export const createAuthedAxios = (config = {}) => attachAuthRefresh(axios.create({ withCredentials: true, ...config }));
